import React, { useEffect } from 'react';
import { useDiagramStore } from '../stores/diagramStore';
import { useEntitlements } from '../hooks/useEntitlements';
import { checkShapeLimit, getShapeLimitFromEntitlements } from '../utils/shapeLimitUtils';

/**
 * Component that sets up shape limit checking in the diagram store
 * This should be rendered at the app level to enable shape limit enforcement
 */
export function ShapeLimitProvider({
  children,
  diagramId,
}: {
  children: React.ReactNode;
  diagramId?: string;
}) {
  const { entitlements } = useEntitlements(diagramId);
  /* WHY: subscribe only to the setter so Zustand state churn doesn’t bubble through the whole tree */
  const setShapeLimitChecker = useDiagramStore((state) => state.setShapeLimitChecker);

  useEffect(() => {
    if (entitlements) {
      const maxShapes = getShapeLimitFromEntitlements(entitlements);
      
      // Create a shape limit checker function
      const checker = (config: { maxShapes: number | null; currentCount: number }) => {
        // Use the limit from entitlements if available, otherwise use the config
        const effectiveLimit = maxShapes !== null ? maxShapes : config.maxShapes;
        
        return checkShapeLimit({
          maxShapes: effectiveLimit,
          currentCount: config.currentCount
        });
      };
      
      setShapeLimitChecker(checker);
      return () => setShapeLimitChecker(undefined);
    }
    setShapeLimitChecker(undefined);
    return undefined;
  }, [entitlements, setShapeLimitChecker]);

  return <>{children}</>;
}
